import React, { useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function EvidenceUploadModal({ 
  isOpen, 
  onClose, 
  assessmentId, 
  onSuccess 
}) {
  const [formData, setFormData] = useState({
    executive_title: '',
    approval_date: '',
    evidence_type: 'email',
    description: '',
    file: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Send JSON data (file upload will be handled separately later)
      const submitData = {
        executive_title: formData.executive_title,
        approval_date: formData.approval_date,
        evidence_type: formData.evidence_type,
        description: formData.description,
        file_name: formData.file ? formData.file.name : null,
        file_size: formData.file ? formData.file.size : null,
        mime_type: formData.file ? formData.file.type : null
      };

      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${assessmentId}/evidence`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      const data = await res.json();
      if (data.success) {
        onSuccess(data.message);
        onClose();
        // Reset form
        setFormData({
          executive_title: '',
          approval_date: '',
          evidence_type: 'email',
          description: '',
          file: null
        });
      } else {
        setError(data.message || 'Failed to upload evidence');
      }
    } catch (err) {
      setError('Error uploading evidence');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Upload Senior Executive Approval Evidence
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Executive Title */}
          <div>
            <label htmlFor="executive_title" className="block text-sm font-medium text-gray-900 mb-2">
              Senior Executive Title *
            </label>
            <select
              id="executive_title"
              name="executive_title"
              value={formData.executive_title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Executive Title</option>
              <option value="CEO">CEO</option>
              <option value="President">President</option>
              <option value="CFO">CFO</option>
              <option value="COO">COO</option>
              <option value="CRO">CRO (Chief Risk Officer)</option>
              <option value="Board Chair">Board Chair</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Approval Date */}
          <div>
            <label htmlFor="approval_date" className="block text-sm font-medium text-gray-900 mb-2">
              Approval Date *
            </label>
            <input
              type="date"
              id="approval_date"
              name="approval_date"
              value={formData.approval_date}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Date when the approval was actually given</p>
          </div>

          {/* Evidence Type */}
          <div>
            <label htmlFor="evidence_type" className="block text-sm font-medium text-gray-900 mb-2">
              Evidence Type *
            </label>
            <select
              id="evidence_type"
              name="evidence_type"
              value={formData.evidence_type}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="email">Email</option>
              <option value="meeting_notes">Meeting Notes</option>
              <option value="document">Document</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* File Upload */}
          <div>
            <label htmlFor="file" className="block text-sm font-medium text-gray-900 mb-2">
              Evidence File
            </label>
            <input
              type="file"
              id="file"
              name="file"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">PDF, DOC, TXT, or image files</p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-900 mb-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of the approval context..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.executive_title || !formData.approval_date}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Uploading...
                </div>
              ) : (
                'Upload Evidence'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
